import { queryOptions, useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedHubsListFilters } from '@/api/apis/facilities';
import { seedHubFromList } from '@/queries/hub';
import { getEmployeesByIds } from '@/queries/employee';
import { queryClient } from '@/queries';
import { hubsQueryKeys } from '@/queries/hub/hub.keys';

const allHubsPaginationParams: PaginatedHubsListFilters = {
  itemsPerPage: 100,
  pageIndex: 0,
  orderBy: 'name',
  asc: true,
};

const getHubListWithEmployee = async (pagination: PaginatedHubsListFilters) => {
  const data = await api.hub.getHubList(pagination);

  if (data) {
    const employeeList = data.items.map((h) => [h.nationalManagerId, h.regionalManagerId]).flat();
    const employees = await getEmployeesByIds(employeeList);

    if (employees) {
      const hubsList = data.items.map((h) => ({
        ...h,
        regionalManager: employees.items.find((e) => e.id === h.regionalManagerId),
        nationalManager: employees.items.find((e) => e.id === h.nationalManagerId),
      }));

      seedHubFromList(hubsList);

      return {
        ...data,
        items: hubsList,
      };
    }
  }

  return data;
};

const HUB_DATA_STALE_TIME = 60 * 1000 * 10; // 10 min

export const hubQueries = {
  all: () =>
    queryOptions({
      queryKey: hubsQueryKeys.getList(allHubsPaginationParams),
      queryFn: () => getHubListWithEmployee(allHubsPaginationParams),
      staleTime: HUB_DATA_STALE_TIME,
      refetchOnWindowFocus: false,
    }),
  list: (pagination: PaginatedHubsListFilters) =>
    queryOptions({
      queryKey: hubsQueryKeys.getList(pagination),
      queryFn: () => getHubListWithEmployee(pagination),
      placeholderData: keepPreviousData,
    }),
  item: (hubId: string) =>
    queryOptions({
      queryKey: hubsQueryKeys.getItem(hubId),
      queryFn: async () => {
        const list = await queryClient.ensureQueryData(hubQueries.all());

        return list.items.find((h) => h.id === hubId) || null;
      },
      staleTime: HUB_DATA_STALE_TIME,
    }),
};

const useHubsQuery = () => {
  return useQuery(hubQueries.all());
};

const useHubsListQuery = (
  pagination: PaginatedHubsListFilters,
  options: {
    enabled?: boolean;
  } = {},
) => {
  return useQuery({ ...hubQueries.list(pagination), ...options });
};

const useHubItemByIdQuery = (id: string) => {
  return useQuery(hubQueries.item(id));
};

export { useHubsQuery, useHubsListQuery, useHubItemByIdQuery };
